import React, { PureComponent } from "react";

import moment from "moment";
import Select from "react-select";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { Modal } from "react-bootstrap";

import { Accordion, Button, ButtonGroup, Dropdown, Icon } from "aible";

import SmallDateChart from "components/Tiles/Sense/SmallDateChart";
import { PieChart, Pie, Cell } from "recharts";

import { getIsCurrentDate, getIsReferenceDate } from "containers/Next/NextReducer";
import { getDefaultDateIndices } from "@/containers/ChatGPT/reducer";

import { LineInput } from "components/Inputs/LineInput";
import Tooltip from "components/Inputs/Tooltip";

import styles from "./style.module.css";

export default class TestTrainSplit extends React.Component {
  constructor(props) {
    super(props);
  }

  renderSplitSelect() {
    const {
      id,
      dataset,
      datasetRankedFields,

      updateDateValue,
      updateDateFilterValue,

      comparisonOptions,
      updateComparisonOptions
    } = this.props;

    const dateField = comparisonOptions?.dateField;

    if (!dateField) {
      return <React.Fragment />;
    }

    const dateFieldVerbose = datasetRankedFields.find((f) => f.label === dateField);

    const dates = dateFieldVerbose?.data ?? [];

    const lastIndex = Math.max(0, dates.length - 1);
    const secondLastIndex = Math.max(0, dates.length - 2);

    const referenceStart = comparisonOptions?.referenceStart ?? dates[0]?.value;
    const referenceEnd = comparisonOptions?.referenceEnd ?? dates[secondLastIndex]?.value;
    const currentStart = comparisonOptions?.currentStart ?? dates[secondLastIndex]?.value;
    const currentEnd = comparisonOptions?.currentEnd ?? dates[lastIndex]?.value;

    const dateRowsExcluded = dates
      .filter(
        (d) =>
          !getIsCurrentDate(undefined, undefined, d.value, comparisonOptions) &&
          !getIsReferenceDate(undefined, undefined, d.value, comparisonOptions)
      )
      .reduce((a, b) => a + b.count, 0);

    const dateRowsBefore = dates
      .filter((d) => getIsReferenceDate(undefined, undefined, d.value, comparisonOptions))
      .reduce((a, b) => a + b.count, 0);

    const dateRowsAfter = dates
      .filter((d) => getIsCurrentDate(undefined, undefined, d.value, comparisonOptions))
      .reduce((a, b) => a + b.count, 0);

    const dateFieldMax = dateFieldVerbose?.data.reduce((a, b) => a + b.count, 0);

    const pieData = [
      {
        name: "rows ignored",
        value: dateRowsExcluded,
        color: "#C8CAD4"
      },
      {
        name: " training rows",
        value: dateRowsBefore,
        color: "#8884d8"
      },
      {
        name: " comparison rows",
        value: dateRowsAfter,
        color: "#34D399"
      }
    ];

    return (
      <React.Fragment>
        <div className={styles.testTrainSection + " " + styles.center}>
          <SmallDateChart
            datasetId={dataset?.id}
            field={dateFieldVerbose}
            comparisonOptions={comparisonOptions}
            updateComparisonOptions={updateComparisonOptions}
            isDisabled={isFinite(id)}
            currentLabel={"Evaluation"}
            referenceLabel={"Training"}
          />
        </div>
        <div className={styles.dateExtraWrapper}>
          <div className={styles.dateExtra}>
            {dateRowsExcluded !== undefined && (
              <div className={styles.dateInfo}>
                <div className={styles.dateDot} style={{ background: "#C8CAD4" }} />
                <div className={styles.dateMessage}>
                  <span style={{ fontWeight: "bold" }}>{dateRowsExcluded}</span>
                  <span style={{ marginLeft: "5px" }}>
                    {"rows ignored (including missing rows which cannot be selected)"}
                  </span>
                </div>
                <Tooltip
                  overlay={
                    <div className={styles.dateFieldTooltip}>
                      {dateRowsExcluded > 0
                        ? `Your AI will exclude rows not in the training or evaluation periods`
                        : `Your AI will not exclude any rows`}
                    </div>
                  }
                />
              </div>
            )}
            <div className={styles.dateInfo}>
              <div className={styles.dateDot} style={{ background: "#8884d8" }} />
              <div className={styles.dateMessage}>
                <span style={{ fontWeight: "bold" }}>{dateRowsBefore}</span>
                <span style={{ marginLeft: "5px" }}>
                  {dateRowsBefore
                    ? `rows from ${referenceStart} and ${referenceEnd} ` + ` will be used for AI training`
                    : "training rows"}
                </span>
              </div>
              <Tooltip
                overlay={
                  <div className={styles.dateFieldTooltip}>
                    {dateRowsBefore === dateFieldMax
                      ? `Your AI will be trained using all ${dateRowsBefore} rows`
                      : `Your AI will be trained using the ${dateRowsBefore} rows ` +
                        `from ${referenceStart} to ${referenceEnd}`}
                  </div>
                }
              />
            </div>
            {dateRowsAfter > 0 && (
              <div className={styles.dateInfo}>
                <div className={styles.dateDot} style={{ background: "#34D399" }} />
                <div className={styles.dateMessage}>
                  <span style={{ fontWeight: "bold" }}>{dateRowsAfter}</span>
                  <span style={{ marginLeft: "5px" }}>
                    {`rows from ${currentStart} and ${currentEnd} ` + ` will be used for AI evaluation`}
                  </span>
                </div>
                <Tooltip
                  overlay={
                    <div className={styles.dateFieldTooltip}>
                      {`After your AI is trained, it will be tested using the ${dateRowsAfter} rows from ` +
                        `${referenceStart} to ${referenceEnd}`}
                    </div>
                  }
                />
              </div>
            )}
          </div>
          <div className={styles.datePieChart}>
            <PieChart width={100} height={100}>
              <Pie data={pieData} labelLine={false} dataKey={"value"} paddingAngle={0}>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </div>
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { id, dataset, datasetRankedFields, comparisonOptions, updateComparisonOptions } = this.props;

    const dateField = comparisonOptions?.dateField;

    const dateFieldOptions =
      datasetRankedFields !== undefined
        ? datasetRankedFields
            .filter((f) => f.type === "date")
            .map((f) => ({
              value: f.label,
              label: f.label
            }))
        : [];

    return (
      <div className={styles.testTrainSplitWrapper}>
        <div className={styles.testTrainSection}>
          <div className={styles.testTrainTitle}>
            {"Which date field do you want to use to delineate your training and evaluation rows?"}
          </div>
          <div className={styles.testTrainSelect}>
            <Select
              options={dateFieldOptions}
              onChange={(val) => {
                const retComparisonOptions = {
                  ...comparisonOptions
                };
                const dateField = datasetRankedFields?.find((f) => f.label === val?.value);
                if (dateField) {
                  const dates = dateField?.data ?? [];
                  const filteredDates = dates?.filter((d) => d?.value !== "{Missing}");

                  const missingDateRange =
                    !retComparisonOptions.referenceStart ||
                    !retComparisonOptions.referenceEnd ||
                    !retComparisonOptions.currentStart ||
                    !retComparisonOptions.currentEnd;

                  if (missingDateRange) {
                    // If missing any dates, use the default state
                    const datasetTimeBucket = dataset?.options?.kpi_analysis?.time_bucket;

                    const { refStart, refEnd, currStart, currEnd } = getDefaultDateIndices(
                      filteredDates,
                      datasetTimeBucket
                    );

                    retComparisonOptions.referenceStart = filteredDates[refStart]?.value;
                    retComparisonOptions.referenceEnd = filteredDates[refEnd]?.value;
                    retComparisonOptions.currentStart = filteredDates[currStart]?.value;
                    retComparisonOptions.currentEnd = filteredDates[currEnd]?.value;
                  }
                } else {
                  const DELETE_FIELDS = ["dateField", "referenceStart", "referenceEnd", "currentStart", "currentEnd"];
                  DELETE_FIELDS.forEach((k) => {
                    delete retComparisonOptions[k];
                  });
                }
                updateComparisonOptions(retComparisonOptions);
              }}
              menuPlacement={"top"}
              value={dateFieldOptions.find((o) => o.label === dateField)}
              placeholder="Select a Date Field"
              isClearable
              isDisabled={isFinite(id)}
            />
          </div>
        </div>
        {!dateField || (isFinite(id) && !comparisonOptions?.referenceStart) ? (
          <div className={styles.testTrainSection} style={{ paddingBottom: "20px" }}>
            {isFinite(id)
              ? "No customized training and evaluation rows"
              : "Select a date field to view the filtering options"}
          </div>
        ) : (
          this.renderSplitSelect()
        )}
      </div>
    );
  }
}
